import React, { useState } from "react";
import classNames from "classnames";

import { fieldByUuid } from "../data/fieldRoles";
import { downloadCsv } from "../model/csv";
import { IssueReport, RecordIssue, toProblemRecords } from "../model/issues";
import {
  MappingKey,
  ReportConfiguration,
  ReportingField,
} from "../model/types";

const RECORDS_SHOWN = 25;

const exportIssue = (
  issue: RecordIssue,
  rows: ReturnType<typeof toProblemRecords>,
  evidenceHeadings: { mapping: MappingKey; label: string }[],
) => {
  const header = [
    "Campaign",
    "Creator",
    "Created date",
    "Allocation date",
    "Status",
    ...evidenceHeadings.map(({ label }) => label),
    "Task URL",
  ];
  const body = rows.map((row) => [
    row.campaign,
    row.creator,
    row.createdDate,
    row.date,
    row.status,
    ...evidenceHeadings.map(({ mapping }) => row.evidence[mapping] ?? ""),
    row.href ? new URL(row.href, window.location.origin).toString() : "",
  ]);
  downloadCsv(`${issue.key}.csv`, header, body);
};

interface DataQualityPanelProps {
  report: IssueReport;
  configuration: ReportConfiguration;
  fields: ReportingField[];
  workspaceUuid: string;
}

/**
 * Data quality, expressed as "which records are wrong and why" rather than as a set of
 * percentages you cannot act on.
 *
 * Each defect expands into the records responsible, linked to the task, so the path from
 * "margin looks off" to "these six rows have no cost" is a couple of clicks.
 */
export default function DataQualityPanel(props: DataQualityPanelProps) {
  const { report, configuration, fields, workspaceUuid } = props;
  const [openIssue, setOpenIssue] = useState<string | null>(null);

  const percent = Math.round(report.score * 100);
  const isClean = report.defects.length === 0;
  const contributingIssue: RecordIssue = {
    key: "contributingRecords",
    label: "Contributing records",
    evidenceMappings: ["date", "budgetMonth"],
    impact: "Included in the totals for the current report view.",
    records: report.contributingRecords,
  };

  const renderRecords = (issue: RecordIssue) => {
    const allRows = toProblemRecords(
      issue.records,
      configuration,
      issue.evidenceMappings,
      workspaceUuid,
    );
    const rows = allRows.slice(0, RECORDS_SHOWN);
    const evidenceHeadings = issue.evidenceMappings.map((mapping) => ({
      mapping,
      label:
        fieldByUuid(fields, configuration.mappings[mapping])?.name.trim() ||
        "Not mapped",
    }));

    const renderEvidence = (value: string | number | null | undefined) => {
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
      ) {
        return String(value);
      }

      return (
        <span className="finance-reporting-issue__missing-value">Missing</span>
      );
    };

    return (
      <div className="finance-reporting-issue__records">
        <div className="finance-reporting-issue__records-toolbar">
          <span>
            Showing {rows.length} of {allRows.length}, newest first
          </span>
          <button
            type="button"
            className="finance-reporting-export"
            onClick={() => exportIssue(issue, allRows, evidenceHeadings)}
          >
            Export all as CSV
          </button>
        </div>
        <table className="finance-reporting-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Creator</th>
              <th>Created date</th>
              <th>Allocation date</th>
              <th>Status</th>
              {evidenceHeadings.map(({ mapping, label }) => (
                <th key={mapping}>{label}</th>
              ))}
              <th aria-label="Open task" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.campaign}</td>
                <td>{row.creator}</td>
                <td>{row.createdDate}</td>
                <td>{row.date}</td>
                <td>{row.status}</td>
                {evidenceHeadings.map(({ mapping }) => (
                  <td key={mapping}>{renderEvidence(row.evidence[mapping])}</td>
                ))}
                <td>
                  {row.href && (
                    <a
                      className="finance-reporting-issue__link"
                      href={row.href}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open task
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderIssue = (issue: RecordIssue, isContributing = false) => {
    const isOpen = openIssue === issue.key;

    return (
      <li
        key={issue.key}
        className={classNames("finance-reporting-issue", {
          "finance-reporting-issue--open": isOpen,
        })}
      >
        <button
          type="button"
          className="finance-reporting-issue__summary"
          aria-expanded={isOpen}
          onClick={() => setOpenIssue(isOpen ? null : issue.key)}
        >
          <span
            className={classNames("finance-reporting-issue__count", {
              "finance-reporting-issue__count--clean": isContributing,
            })}
          >
            {issue.records.length}
          </span>
          <span className="finance-reporting-issue__text">
            <span className="finance-reporting-issue__label">
              {issue.label}
            </span>
            <span className="finance-reporting-issue__impact">
              {issue.impact}
            </span>
          </span>
          <span className="finance-reporting-issue__chevron" aria-hidden="true">
            {isOpen ? "−" : "+"}
          </span>
        </button>
        {isOpen && renderRecords(issue)}
      </li>
    );
  };

  return (
    <div className="finance-reporting-quality-panel">
      <div className="finance-reporting-quality-panel__head">
        <div>
          <p className="finance-reporting-eyebrow">Traceability</p>
          <h3>Data quality</h3>
          <p className="finance-reporting-quality-panel__note">
            {isClean
              ? `All ${report.contributingCount} contributing records are complete.`
              : `${report.goodContributingCount} good contributing records and ${report.attentionCount} records need attention. Fix the records below to reach 100%.`}
          </p>
        </div>
        <span
          className={classNames("finance-reporting-quality-panel__score", {
            "finance-reporting-quality-panel__score--clean": percent === 100,
          })}
        >
          {percent}%
        </span>
      </div>

      <ul className="finance-reporting-issues">
        {report.defects.map((issue) => renderIssue(issue))}
        {renderIssue(contributingIssue, true)}
      </ul>
    </div>
  );
}
